/*
 * HTTP operations for accessing the VP cloud backend.
*/

import invariant from 'invariant';
import { getLogger } from '../common/logger';
import { signInRedirect, getAuthenticatedUser } from '../component/oidc/usermanager/oidc-client-usermanager';
import { checkStatus, httpStatusCodes } from './http-common';

const logger = getLogger('cloud-backend');

/**
 * Adds/replaces headers in options in a non-mutating manner.
 *
 * @param options - The Fetch options object
 * @param headers - An object containing the headers to replace
 */
const addHeaders = (options, headers) => ({ ...options, headers: { ...options.headers, ...headers } });

/**
 * Performs fetch() calls to the cloud backend, deserializing the response JSON.
 * @param path
 * @param options
 */

const doFetch = async (url, options) => {
  if (!url.match(/^https?/)) {
    // If the URL does NOT start with http or https it is considered an API path.
    invariant(url.match(/^\/.*$/), 'API path must start with a slash');
    url = `${baseUrl}${url}`;
  }

  logger.debug('Performing', options.method, url, options.body ? `with body ${options.body}` : '');
  const user = await getAuthenticatedUser();
  if (user) {
    logger.debug('Adding Authorization header for access token');
    options = addHeaders(options, { Authorization: `Bearer ${user.access_token}` });
  }
  options = addHeaders(options, { 'VP-TimeZoneOffset': new Date().getTimezoneOffset() * -1 });
  const response = await fetch(url, options);
  if (response.status === httpStatusCodes.unauthorized) {
    signInRedirect();
  }
  return checkStatus(response);
};

const doFetchPromise = (resolve, reject, response) => {
  response.json()
    .then((json) => {
      // Currently reject is not used here as there are not promise rejection functionalities yet
      resolve(json);
    })
    .catch(() => {
      resolve(null);
    });
};

/**
 * Performs fetch() calls to the cloud backend, deserializing the response JSON.
 *
 * This implementation optionally converts the JSON response's
 * property names to camelCase.
 *
 * @param path
 * @param options
 */
const doFetchJson = function (path, options) {
  return doFetch(path, options)
    .then((response) => new Promise((resolve, reject) => doFetchPromise(resolve, reject, response)));
};

/**
 * Performs a GET request to the given path, and returns the deserialized JSON.
 *
 * @param path
 * @param options
 */

const DEFAULT_GET_OPTIONS = {
  method: 'GET',
  headers: {},
};

export const getJson = (path, options = {}) => {
  const mergedOptions = { ...DEFAULT_GET_OPTIONS, ...options };
  return doFetchJson(path, mergedOptions);
};

/* POST */

const DEFAULT_POST_OPTIONS = {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
};

/**
 * Performs a POST request to the given path and with the given data.
 *
 * @param path
 * @param data
 * @param options
 */

export const postJson = (path, resourceObject, options = {}) => {
  const mergedOptions = { ...DEFAULT_POST_OPTIONS, ...options, body: JSON.stringify(resourceObject) };
  return doFetchJson(path, mergedOptions);
};

/* PUT */

const DEFAULT_PUT_OPTIONS = {
  method: 'PUT',
  headers: { 'Content-Type': 'application/json' },
};

/**
 * Performs a PUT request to the given path and with the given data.
 *
 * @param path
 * @param data
 * @param options
 */

export const putJson = (path, resourceObject, options = {}) => {
  const mergedOptions = { ...DEFAULT_PUT_OPTIONS, ...options, body: JSON.stringify(resourceObject) };
  return doFetchJson(path, mergedOptions);
};

/* Delete */

const DEFAULT_DELETE_OPTIONS = {
  method: 'DELETE',
  headers: {},
};

/**
 * Performs a DELETE request to the given path and with the given data.
 *
 * @param path
 * @param data
 * @param options
 */

export const deleteJson = (path, options = {}) => {
  options = { ...DEFAULT_DELETE_OPTIONS, ...options };
  return doFetchJson(path, options);
};

/**
 * Extracts the response's data or throws an Error in case of violations.
 * @param response
 * @returns {response.data|{fullName, id, profilePhotoUrl}}
 */

export const extractResponseData = (response) => {
  const violations = response.error;
  if (violations && violations.length > 0) {
    const error = new Error(violations);
    throw error;
  } else {
    return response;
  }
};
